import { CompositionRows } from '../components/LeadTimeBreakdown';
import { T, Var, useGT } from 'gt-react';
import type { SuggestionCartStatus } from '@erp/rpc';
import type { BoardSharedFilters } from '../utils/boardSharedFilters';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import {
  ArrowSquareOutIcon,
  CaretLeftIcon,
  CaretRightIcon,
  XIcon,
} from '@phosphor-icons/react';
import {
  useSuggestionDetail,
  useSupplierAssignments,
  useSourceTransferAssignments,
  type SuggestionOption,
  type GetSuggestionDetailResponse,
  type AssignmentLine,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { CopyIdButton } from 'src/components/common/CopyIdButton';
import { ExpectedArrival } from '../components/LeadTimeDetail';
import { BuyLegPriceBreak } from '../components/PriceBreakNudgeChip';
import { OptionRationale } from '../components/OptionRationale';
import { BelowVendorMinBadge } from '../components/BelowVendorMinBadge';
import { NeedInsightsPanel } from './NeedInsightsPanel';
import { OvershootBadge } from './OvershootBadge';
import { CartMinimumStatus } from './CartMinimumStatus';
import { ContractPricingDetail } from './ContractPricingDetail';
import { formatUnitCost } from '../components/unitCostFormat';
import { assignmentBulkEditCartKey } from '../utils/assignmentBulkEditSession';
import { OverrideSection } from './OverrideSection';
import { SalesOrderInline } from '../components/SalesOrderInline';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PRODUCT_BY_KEY_SEGMENT } from '../../product-detail/productIdentity';
import { cn } from '@/lib/utils';
import {
  parseChipString,
  filterAssignmentLines,
  getLineDesignation,
} from '../utils/lineFilters';
import { isReleasedStatus } from '../utils/releasedStatus';
import {
  transferSiblingSourceId,
  transferSiblingsInput,
} from '../components/supplierAssignmentsInput';

function formatCurrency(v: number | null | undefined) {
  if (v == null) return '—';
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumber(v: number | null | undefined) {
  if (v == null) return '—';
  return v.toLocaleString();
}

// Quantities are stocked/ordered in whole units — round for display so the raw
// forecast decimals (e.g. 2.4085 units) don't surface on the review screen.
function formatQty(v: number | null | undefined) {
  return formatNumber(v == null ? null : Math.round(v));
}

function planLegActionLabel(
  legType: string,
  gt: ReturnType<typeof useGT>,
): string {
  if (legType === 'buy') return gt('Buy', { context: 'option action label' });
  if (legType === 'stock') {
    return gt('In stock', { context: 'option action label' });
  }
  if (legType === 'substitute') {
    return gt('Substitute', {
      context: 'option action label: another product fills the demand',
    });
  }
  if (legType === 'make') {
    return gt('Make', {
      context: 'option action label: a recipe produces the product',
    });
  }
  return gt('Transfer', { context: 'option action label' });
}

function planLegContext(leg: SuggestionOption['legs'][number]): string {
  if (leg.legType === 'stock' && leg.unitNumber) return ` · ${leg.unitNumber}`;
  if (leg.legType === 'transfer' && leg.sourceWarehouseName) {
    return ` · ${leg.sourceWarehouseName}`;
  }
  if (leg.legType === 'buy' && leg.supplierName) {
    return ` · ${leg.supplierName}`;
  }
  return '';
}

function destArrow(name: string | null | undefined): string {
  return name ? ` → ${name}` : '';
}

interface OptionCardProps {
  option: SuggestionOption;
  // Active = the option that's currently the suggestion's decision (the buyer's
  // explicit choice, or the engine's recommended default when none is chosen).
  isSelected: boolean;
  // True only when the buyer explicitly chose this one (vs the recommended
  // default). Clicking an explicitly-chosen option clears it (back to default).
  isExplicitlyChosen: boolean;
  neededQuantity: number;
  onApply: (optionId: string | null) => void;
  isApplying: boolean;
  isAnyApplying: boolean;
  // Read-only: render the option as a static summary (the single recommended
  // action) — no click-to-select/toggle. Used by the direct portals.
  readOnly?: boolean;
  // The live cart of the suggestion's effective buy. Shown on the card whose
  // buy ships in that cart, in place of the leg's frozen below-minimum gap.
  cart?: SuggestionCartStatus | null;
  // When set, the lead-time value becomes a click-through into the observed
  // lead-time calculation detail (rung ladder) for this product × warehouse.
  leadTime?: {
    productV3Key: string | null;
    warehouseId: string;
    productLabel: string | null;
    /** Live-resolved active override, shown over the option's saved estimate. */
    overrideDays: number | null;
    suggestionId?: string;
    highlightCartKey?: string | null;
  };
  gt: ReturnType<typeof useGT>;
}

function OptionCard({
  option,
  isSelected,
  isExplicitlyChosen,
  neededQuantity,
  onApply,
  isApplying,
  isAnyApplying,
  readOnly = false,
  leadTime,
  cart = null,
  gt,
}: OptionCardProps) {
  const actionLabels: Record<string, string> = {
    buy: gt('Buy', { context: 'option action label' }),
    transfer: gt('Transfer', { context: 'option action label' }),
    buy_and_transfer: gt('Buy + Transfer', { context: 'option action label' }),
    stock: gt('In stock', { context: 'option action label' }),
  };
  const stockLeg = option.legs.find((l) => l.legType === 'stock') ?? null;
  const buyLeg = option.legs.find((l) => l.legType === 'buy') ?? null;
  const transferUnit = option.legs.find(
    (l) => l.legType === 'transfer' && l.unitNumber != null,
  );

  // Clicking the explicitly-chosen option clears it (revert to the recommended
  // default); clicking any other option selects it.
  const apply = () => {
    if (isAnyApplying) return;
    onApply(isExplicitlyChosen ? null : option.optionId);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !isAnyApplying) {
      e.preventDefault();
      apply();
    }
  };

  return (
    <div
      role={readOnly ? undefined : 'button'}
      tabIndex={readOnly ? undefined : 0}
      onClick={readOnly ? undefined : apply}
      onKeyDown={readOnly ? undefined : handleKeyDown}
      aria-pressed={readOnly ? undefined : isSelected}
      aria-busy={isApplying}
      className={cn(
        'rounded-lg border p-4 transition-colors',
        !readOnly && 'cursor-pointer',
        isSelected
          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
          : cn('border-gray-200 bg-white', !readOnly && 'hover:bg-gray-50'),
        isAnyApplying && 'opacity-60 pointer-events-none',
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              option.action === 'transfer'
                ? 'bg-purple-100 text-purple-700'
                : option.action === 'buy'
                  ? 'bg-blue-100 text-blue-700'
                  : option.action === 'stock'
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-indigo-100 text-indigo-700'
            }`}
          >
            {actionLabels[option.action] ?? option.action}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-xs ${
              option.source === 'engine'
                ? 'bg-gray-100 text-gray-600'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {option.source === 'engine'
              ? gt('Engine', { context: 'option source badge' })
              : gt('Manual', { context: 'option source badge' })}
          </span>
          {option.rank > 0 && (
            <span className="text-xs text-gray-400">#{option.rank}</span>
          )}
          {/* Two orthogonal axes, each shown once:
              · Recommended = the engine's top pick (rank-1 engine option),
                shown regardless of selection so it stays visible after an
                override.
              · Active in cart = this is the suggestion's current decision
                (engine default or buyer's explicit choice). When the buyer
                overrides, "Active in cart" moves to the chosen option while
                "Recommended" stays on the engine's pick — so the two badges
                visibly diverge instead of doubling up. */}
          {option.source === 'engine' && option.rank === 1 && (
            <span
              className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700"
              title={gt('The engine’s top recommendation', {
                context: 'engine recommended badge tooltip',
              })}
            >
              <T context="engine recommended option badge">Recommended</T>
            </span>
          )}
          {isSelected && !readOnly && (
            <span
              className="rounded-full bg-blue-600 px-2 py-0.5 text-xs font-medium text-white"
              title={gt('Active in the cart right now', {
                context: 'active in cart badge tooltip',
              })}
            >
              <T context="active in cart option badge">Active in cart</T>
            </span>
          )}
          {isApplying && (
            <span
              className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
              aria-label={gt('Applying', {
                context: 'mutation in-flight label',
              })}
            />
          )}
          <CopyIdButton
            id={option.optionId}
            label={gt('Copy option ID', { context: 'copy id button label' })}
          />
          {option.supplierId && (
            <CopyIdButton
              id={option.supplierId}
              label={gt('Copy supplier ID', {
                context: 'copy id button label',
              })}
            />
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {(option.action === 'buy' || option.action === 'buy_and_transfer') && (
          <>
            <div>
              <span className="text-gray-500">
                {gt('Supplier', { context: 'option detail label' })}
              </span>
              <p className="font-medium">{option.supplierName ?? '—'}</p>
            </div>
            <div>
              <span className="text-gray-500">
                {gt('Ship to', { context: 'option detail label' })}
              </span>
              <p className="font-medium">{option.shipToWarehouseName ?? '—'}</p>
            </div>
            <div>
              <span className="text-gray-500">
                {gt('Buy qty', { context: 'option detail label' })}
              </span>
              <p className="font-medium">{formatQty(option.buyQuantity)}</p>
            </div>
            <div>
              <span className="text-gray-500">
                {gt('Unit cost', { context: 'option detail label' })}
              </span>
              <p className="font-medium">
                {option.unitCost == null
                  ? '—'
                  : formatUnitCost(option.unitCost)}
              </p>
            </div>
            <div>
              <span className="text-gray-500">
                {gt('Total cost', { context: 'option detail label' })}
              </span>
              <p className="font-medium">{formatCurrency(option.totalCost)}</p>
            </div>
            <div>
              <span className="text-gray-500">
                {gt('Freight', { context: 'option detail label' })}
              </span>
              <p className="font-medium">
                {formatCurrency(option.estimatedFreightCost)}
              </p>
            </div>
            <div>
              <span className="text-gray-500">
                {gt('MOQ', {
                  context: 'option detail label - minimum order quantity',
                })}
              </span>
              <p className="font-medium">
                {formatQty(option.vendorMoqQuantity)}
              </p>
            </div>
            <div>
              <span className="text-gray-500">
                {gt('Nearest', {
                  context: 'option detail label - pack/round-up size',
                })}
              </span>
              <p className="font-medium">
                {formatQty(option.vendorIncrementQuantity)}
              </p>
            </div>
            <div>
              <span className="text-gray-500">
                {gt('MOV', {
                  context: 'option detail label - minimum order value',
                })}
              </span>
              <p className="font-medium">
                {formatCurrency(option.vendorMovAmount)}
              </p>
            </div>
            <div className="col-span-2 flex flex-wrap gap-2">
              {cart != null &&
                cart.shipToWarehouseId === option.shipToWarehouseId && (
                  <CartMinimumStatus cart={cart} />
                )}
              <OvershootBadge
                option={option}
                neededQuantity={neededQuantity}
                gt={gt}
              />
              <BelowVendorMinBadge
                buyQuantity={option.buyQuantity}
                vendorMoqQuantity={option.vendorMoqQuantity}
              />
              <BuyLegPriceBreak
                priceBreak={buyLeg?.priceBreak}
                quantity={buyLeg?.quantity}
              />
            </div>
            <ContractPricingDetail
              priceBreak={buyLeg?.priceBreak}
              costAgreement={buyLeg?.costAgreement}
              quantity={buyLeg?.quantity}
            />
          </>
        )}

        {option.action === 'stock' && (
          <>
            <div>
              <span className="text-gray-500">
                {gt('Unit', { context: 'option detail label' })}
              </span>
              <p className="font-mono text-sm font-medium">
                {stockLeg?.unitNumber ?? '—'}
              </p>
            </div>
            <div>
              <span className="text-gray-500">
                {gt('Unit size', { context: 'option detail label' })}
              </span>
              <p className="font-medium">
                {formatQty(stockLeg?.unitQuantity ?? null)}
              </p>
            </div>
            <div className="col-span-2">
              <span className="text-gray-500">
                {gt('Fulfillment', { context: 'option detail label' })}
              </span>
              <p className="font-medium">
                {stockLeg?.fulfillment === 'cut'
                  ? gt('Cut {qty} from this unit', {
                      context: 'stock cut fulfillment',
                      qty: formatQty(neededQuantity),
                    })
                  : stockLeg?.fulfillment === 'ship_whole'
                    ? gt('Ship whole unit', {
                        context: 'stock ship-whole fulfillment',
                      })
                    : '—'}
              </p>
            </div>
          </>
        )}

        {(option.action === 'transfer' ||
          option.action === 'buy_and_transfer') && (
          <>
            <div>
              <span className="text-gray-500">
                {gt('Source Location', { context: 'option detail label' })}
              </span>
              <p className="flex flex-wrap items-center gap-1 font-medium">
                <span className="min-w-0 break-words">
                  {option.sourceWarehouseName ?? '—'}
                </span>
                {option.sourceWarehouseType === 'hub' && (
                  <span className="shrink-0 rounded bg-gray-200 px-1 text-[10px] uppercase tracking-wide text-gray-700">
                    <T context="hub transfer source marker">hub</T>
                  </span>
                )}
                {option.sourceWarehouseType === 'branch' && (
                  <span className="shrink-0 rounded bg-indigo-100 px-1 text-[10px] uppercase tracking-wide text-indigo-700">
                    <T context="sibling-branch transfer source marker">
                      sibling
                    </T>
                  </span>
                )}
              </p>
            </div>
            <div>
              <span className="text-gray-500">
                {gt('Transfer qty', { context: 'option detail label' })}
              </span>
              <p className="font-medium">
                {formatQty(option.transferQuantity)}
              </p>
            </div>
            <div>
              <span className="text-gray-500">
                {gt('Transfer freight', { context: 'option detail label' })}
              </span>
              <p className="font-medium">
                {formatCurrency(option.transferFreightEstimate)}
              </p>
            </div>
            {transferUnit?.unitNumber != null && (
              <div className="col-span-2">
                <span className="text-gray-500">
                  {gt('Selected unit', { context: 'option detail label' })}
                </span>
                <p className="font-mono text-sm font-medium">
                  {transferUnit.unitNumber}
                </p>
              </div>
            )}
          </>
        )}

        {option.legs.map((leg) => {
          if (
            leg.leadTimeComposition == null &&
            !leg.leadTimeSettingsChangedSinceRun
          ) {
            return null;
          }
          return (
            <div className="col-span-2 space-y-1" key={leg.legId}>
              <p className="text-xs font-medium">
                {gt('Saved buy lead time', {
                  context: 'saved option lead time component breakdown heading',
                })}
              </p>
              {leg.leadTimeComposition && (
                <CompositionRows snapshot={leg.leadTimeComposition} />
              )}
              {leg.leadTimeSettingsChangedSinceRun && (
                <p className="text-xs text-amber-700">
                  {gt(
                    'Settings changed since this option was generated. Regenerate suggestions to update timing and quantities.',
                    {
                      context:
                        'saved buy leg lead time settings mismatch warning',
                    },
                  )}
                </p>
              )}
            </div>
          );
        })}
        {option.estimatedLeadTimeDays != null && (
          <div>
            <span className="text-gray-500">
              {gt('Leg lead-time estimate', {
                context:
                  'option detail label distinguishes a leg estimate from full route arrival',
              })}
            </span>
            {/* Click-through applies to measured/declared vendor lead times
                only — a transfer's flat transit assumption has no calculation
                to show, so it stays a static value. */}
            {leadTime && option.action !== 'transfer' ? (
              <p className="font-medium">
                <ExpectedArrival
                  days={option.estimatedLeadTimeDays}
                  overrideDays={leadTime.overrideDays}
                  productLabel={leadTime.productLabel}
                  productV3Key={leadTime.productV3Key}
                  warehouseId={leadTime.warehouseId}
                  suggestionId={leadTime.suggestionId}
                  highlightCartKey={leadTime.highlightCartKey ?? undefined}
                />
              </p>
            ) : (
              <p className="font-medium">
                <T context="lead time in days, e.g. 3d">
                  <Var>{option.estimatedLeadTimeDays}</Var>d
                </T>
              </p>
            )}
          </div>
        )}
      </div>

      {/* Per-leg breakdown — only for composed plans (pivot, split, or
          multi-transfer). A single-leg buy/transfer is fully described by the
          flat fields above, so we don't repeat it. */}
      {option.legs.length > 1 && (
        <div className="mt-2 border-t border-gray-100 pt-2">
          <span className="text-xs font-medium text-gray-500">
            {gt('Plan legs', { context: 'option leg breakdown heading' })}
          </span>
          <ul className="mt-1 space-y-0.5">
            {[...option.legs]
              .sort((a, b) => a.rank - b.rank)
              .map((leg) => (
                <li key={leg.legId} className="text-xs text-gray-700">
                  <span className="font-medium">
                    {planLegActionLabel(leg.legType, gt)}
                  </span>{' '}
                  {formatQty(leg.quantity)}
                  {planLegContext(leg)}
                  {destArrow(leg.shipToWarehouseName)}
                </li>
              ))}
          </ul>
        </div>
      )}

      {option.rationale && (
        <p className="mt-2 text-xs text-gray-500" title={option.rationale}>
          <OptionRationale rationale={option.rationale} />
        </p>
      )}
    </div>
  );
}

const priorityColors: Record<string, string> = {
  critical: 'bg-red-200 text-red-800',
  urgent: 'bg-red-100 text-red-700',
  standard: 'bg-blue-100 text-blue-700',
  fill: 'bg-gray-100 text-gray-700',
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  ordered: 'bg-blue-100 text-blue-700',
};

/**
 * At-a-glance numbers for one suggestion: product / destination / needed /
 * trigger / on-hand / net position / policy min-max, plus priority + status
 * badges. Rendered standalone for deficit triggers and as a tab next to the
 * Sales Order detail for SO triggers.
 */
function NeedSummaryPanel({
  data,
  gt,
}: {
  data: GetSuggestionDetailResponse;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <div>
          <span className="text-gray-500">
            {gt('Item Description', {
              context: 'suggestion detail item description label',
            })}
          </span>
          <p className="font-medium">{data.productName}</p>
          {data.productSku && (
            <p className="text-xs text-gray-400">{data.productSku}</p>
          )}
        </div>
        <div>
          <span className="text-gray-500">
            {gt('Destination', { context: 'suggestion detail label' })}
          </span>
          <p className="font-medium">{data.destinationWarehouseName}</p>
        </div>
        <div>
          <span className="text-gray-500">
            {gt('Required Qty', { context: 'suggestion detail label' })}
          </span>
          <p className="font-medium">
            {formatNumber(Math.round(data.neededQuantity))}
          </p>
        </div>
        <div>
          <span className="text-gray-500">
            {gt('Trigger', { context: 'suggestion detail label' })}
          </span>
          <p className="font-medium">
            {data.trigger === 'deficit'
              ? gt('Below min', { context: 'suggestion trigger label' })
              : gt('Sales order', { context: 'suggestion trigger label' })}
          </p>
        </div>
        {data.buyerSourceType != null && (
          <div>
            <span className="text-gray-500">
              {gt('Buyer source', { context: 'suggestion detail label' })}
            </span>
            <p className="flex flex-wrap items-center gap-1.5 font-medium">
              {data.buyerSourceType === 'transfer'
                ? gt('Transfer from {warehouse}', {
                    context: 'buyer source label',
                    warehouse: data.buyerSourceWarehouseName ?? '—',
                  })
                : gt('Buy from vendor', { context: 'buyer source label' })}
              {data.buyerSourceHonored ? (
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                  {gt('pinned', { context: 'buyer source honored badge' })}
                </span>
              ) : (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                  {gt('unavailable — fell back', {
                    context: 'buyer source fallback badge',
                  })}
                </span>
              )}
            </p>
          </div>
        )}
        <div>
          <span className="text-gray-500">
            {gt('On hand', { context: 'suggestion detail label' })}
          </span>
          <p className="font-medium">
            {formatNumber(Math.round(data.currentOnHand))}
          </p>
          {data.lotLevelDeficit && (
            <p className="mt-1 text-xs font-medium text-amber-700">
              {gt(
                'Aggregate on-hand is not one piece — no local unit covers this line',
                { context: 'lot-level deficit warning' },
              )}
            </p>
          )}
        </div>
        <div>
          <span className="text-gray-500">
            {gt('Net position', { context: 'suggestion detail label' })}
          </span>
          <p className="font-medium">
            {formatNumber(Math.round(data.currentNetPosition))}
          </p>
        </div>
        {data.reorderPoint != null && (
          <div>
            <span className="text-gray-500">
              {gt('Reorder Pt', {
                context: 'suggestion detail forecast reorder point label',
              })}
            </span>
            <p className="font-medium">
              {formatNumber(Math.round(data.reorderPoint))}
            </p>
          </div>
        )}
        {data.orderUpToLevel != null && (
          <div>
            <span className="text-gray-500">
              {gt('Order up to', {
                context: 'suggestion detail forecast order-up-to label',
              })}
            </span>
            <p className="font-medium">
              {formatNumber(Math.round(data.orderUpToLevel))}
            </p>
          </div>
        )}
        {data.safetyStock != null && (
          <div>
            <span className="text-gray-500">
              {gt('Safety Stock', {
                context: 'suggestion detail forecast safety stock label',
              })}
            </span>
            <p className="font-medium">
              {formatNumber(Math.round(data.safetyStock))}
            </p>
          </div>
        )}
      </div>
      {(data.policyMin != null || data.policyMax != null) && (
        <p className="mt-2 text-xs text-gray-400">
          {gt('AS400 min/max (legacy, not used for sizing): {min} / {max}', {
            context: 'suggestion detail legacy min/max reference note',
            min:
              data.policyMin != null
                ? formatNumber(Math.round(data.policyMin))
                : '—',
            max:
              data.policyMax != null
                ? formatNumber(Math.round(data.policyMax))
                : '—',
          })}
        </p>
      )}
      {(data.reorderPoint != null || data.orderUpToLevel != null) && (
        <p className="mt-1 text-xs text-gray-400">
          {gt(
            'Quantity is sized to the forecast order-up-to level, not AS400 min/max.',
            { context: 'suggestion detail forecast basis explainer' },
          )}
        </p>
      )}
      {data.forecastCalculatedAt && (
        <p className="mt-1 text-xs text-gray-400">
          {gt('Forecast computed {date}', {
            context: 'suggestion detail forecast last-calculated note',
            date: new Date(data.forecastCalculatedAt).toLocaleDateString(),
          })}
        </p>
      )}
      <div className="mt-3 flex items-center gap-2">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${priorityColors[data.priority] ?? ''}`}
        >
          {data.priority}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[data.status] ?? ''}`}
        >
          {data.status}
        </span>
      </div>
    </div>
  );
}

interface SuggestionDetailModalProps {
  // Scopes the modal to one portal's side: 'buy' (Outgoing Purchase Orders)
  // shows only buy options + a buy-only plan; 'transfer' (Outgoing Transfer
  // Orders) only transfer. Undefined (legacy combined page) shows both.
  side?: 'buy' | 'transfer';
  // The board's resolved scope, so prev/next walks the same list the board
  // shows — including its default division, which is not in the URL.
  divisionIds?: string[];
  warehouseIds?: string[];
  buyerCodes?: string[];
  lineFilters?: BoardSharedFilters;
}

export function SuggestionDetailModal({
  side,
  divisionIds,
  warehouseIds,
  buyerCodes,
  lineFilters,
}: SuggestionDetailModalProps = {}) {
  const gt = useGT();
  const navigate = useNavigate();
  const {
    suggestionId,
    supplierId,
    shipTo,
    showTrashed,
    hideReleased,
    transferSourceId,
    sourceId,
    chips,
    q,
    view,
  } = useSearch({
    strict: false,
  }) as {
    suggestionId?: string;
    supplierId?: string;
    shipTo?: string;
    showTrashed?: boolean;
    hideReleased?: boolean;
    transferSourceId?: string;
    /** The transfer board's open source location (GEN-3026). */
    sourceId?: string;
    chips?: string;
    q?: string;
    view?: 'all' | 'po' | 'to';
  };

  const highlightCartKey = assignmentBulkEditCartKey(supplierId, shipTo);

  const detail = useSuggestionDetail(
    { suggestionId: suggestionId ?? '' },
    suggestionId != null,
  );
  const data = detail.data;
  // Options relevant to this portal's side. A branch transfer suggestion only
  // has transfer options and a hub buy suggestion only buy options (cascade),
  // so this is mostly a no-op there — but it keeps the modal honest if a legacy
  // buy_and_transfer option shows up, and drives the count/recommended default.
  const visibleOptions = useMemo(() => {
    if (!data) return [];
    if (!side) return data.options;
    return data.options.filter((o) => o.legs.some((l) => l.legType === side));
  }, [data, side]);

  // Sibling navigation — the prev/next list is the assignment lines of whatever
  // the modal was opened from: a supplier's carts, or (for vendorless
  // transfers) a source warehouse's transfer board. Exactly one is active.
  // Fetched only while the modal is open, and with the board detail's exact
  // input (one cart, same trashed toggle), so it reads the open cart's cached
  // lines instead of loading the supplier's every cart a second time.
  const includeTrashed = showTrashed === true || undefined;
  const supplierSiblings = useSupplierAssignments(
    {
      supplierId: supplierId ?? '',
      divisionIds,
      warehouseIds,
      includeTrashed,
      includeReleased: hideReleased !== true,
      shipToWarehouseId: shipTo,
      buyerCodes,
      ...lineFilters,
    },
    supplierId != null && suggestionId != null,
    true,
  );
  const siblingSourceId = transferSiblingSourceId({
    sourceId,
    transferSourceId,
  });
  const transferSiblings = useSourceTransferAssignments(
    transferSiblingsInput(
      {
        divisionIds,
        warehouseIds,
        includeTrashed,
        includeReleased: hideReleased !== true,
        shipToWarehouseId: shipTo,
        buyerCodes,
        lineFilters,
      },
      { sourceId, transferSourceId },
    ),
    supplierId == null && siblingSourceId != null && suggestionId != null,
    sourceId != null,
  );
  const isTransferSiblings = supplierId == null && siblingSourceId != null;
  const siblingData =
    supplierId != null ? supplierSiblings.data : transferSiblings.data;
  const activeChips = useMemo(() => parseChipString(chips), [chips]);
  // Prev/next is scoped to the ONE cart (supplier × ship-to warehouse, or
  // source × destination for transfers) the open suggestion belongs to — so
  // "X / N" is the row's position within that cart and arrow-keys walk just
  // that cart, in the same order it renders (SO-first, then value desc: cost
  // for buys, transfer qty for transfers).
  const siblingIds = useMemo<string[]>(() => {
    const lines = siblingData?.lines ?? [];
    const carts = siblingData?.carts ?? [];
    // Which cart is open? Derived from the current suggestion's line (use the
    // unfiltered set so we always know its cart, even if a chip would hide it).
    const currentLine = lines.find((l) => l.suggestionId === suggestionId);
    const cartId = currentLine?.shipToWarehouseId ?? null;
    if (cartId == null) return suggestionId ? [suggestionId] : [];

    const isSo = (l: AssignmentLine) => l.trigger === 'sales_order';
    const valueOf = (l: AssignmentLine): number =>
      isTransferSiblings
        ? (l.topOption?.transferQuantity ?? 0)
        : (l.topOption?.totalCost ?? 0);

    // Restrict to that cart, then apply the same chip/search + buy-vs-transfer
    // view filters the board uses ('po' → buy/buy+transfer, 'to' → transfers).
    let cartLines = filterAssignmentLines({
      lines: lines.filter((l) => l.shipToWarehouseId === cartId),
      carts,
      search: q ?? '',
      chips: activeChips,
    });
    if (view === 'po' || view === 'to') {
      cartLines = cartLines.filter((l) => {
        const d = getLineDesignation(l);
        return view === 'to'
          ? d === 'transfer'
          : d === 'buy' || d === 'buy_and_transfer';
      });
    }

    const seen = new Set<string>();
    const out: string[] = [];
    for (const l of [...cartLines].sort((a, b) => {
      const soDiff = (isSo(a) ? 0 : 1) - (isSo(b) ? 0 : 1);
      if (soDiff !== 0) return soDiff;
      return valueOf(b) - valueOf(a);
    })) {
      if (!seen.has(l.suggestionId)) {
        seen.add(l.suggestionId);
        out.push(l.suggestionId);
      }
    }
    // Keep the open suggestion navigable even if a filter would hide it.
    if (suggestionId && !seen.has(suggestionId)) out.unshift(suggestionId);
    return out;
  }, [siblingData, activeChips, q, view, isTransferSiblings, suggestionId]);
  const currentIdx = suggestionId ? siblingIds.indexOf(suggestionId) : -1;
  const prevId = currentIdx > 0 ? siblingIds[currentIdx - 1] : null;
  const nextId =
    currentIdx >= 0 && currentIdx < siblingIds.length - 1
      ? siblingIds[currentIdx + 1]
      : null;
  // Keep sibling-nav / close on the current portal route (the modal renders on
  // both); otherwise the transfers portal bounces to the POs route.
  const detailBasePath =
    side === 'transfer'
      ? '/procurement/internal-transfers'
      : '/procurement/replenishment';
  const gotoSibling = (id: string) => {
    void navigate({
      to: detailBasePath,
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        suggestionId: id,
      }),
      resetScroll: false,
    });
  };

  // Keyboard ← / → navigates siblings. Ignored when focus is in a form control
  // so typing in override inputs doesn't accidentally skip needs.
  useEffect(() => {
    if (!suggestionId || siblingIds.length <= 1) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const t = document.activeElement;
      const tag = t?.tagName ?? '';
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (t instanceof HTMLElement && t.isContentEditable)
      ) {
        return;
      }
      const target = e.key === 'ArrowLeft' ? prevId : nextId;
      if (target) {
        e.preventDefault();
        gotoSibling(target);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionId, prevId, nextId, siblingIds.length]);

  const handleClose = () => {
    void navigate({
      to: detailBasePath,
      search: (prev: Record<string, unknown>) => {
        const { suggestionId: _omit, ...rest } = prev;
        return rest;
      },
      resetScroll: false,
    });
  };

  if (!suggestionId) return null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      {/* The built-in close sits at the popup's top-right, underneath this
          modal's sticky z-10 header — visible nowhere, so the only way out was
          Escape or the backdrop. Suppressed here and re-hung in the header
          itself, beside the sibling controls. */}
      <DialogContent
        className="h-[90vh] w-[95vw] max-w-[1440px] overflow-hidden p-0 sm:max-w-[1440px]"
        showCloseButton={false}
      >
        <div className="grid h-full min-h-0 grid-cols-[420px_1fr] divide-x">
          {/* Left: decision-support insights (popularity, inventory, sales). */}
          <div className="flex h-full min-h-0 flex-col overflow-y-auto">
            <NeedInsightsPanel suggestionId={suggestionId} />
          </div>
          {/* Middle: engine fulfillment options (the candidate plans). */}
          <div className="flex h-full min-h-0 flex-col overflow-y-auto">
            <DialogHeader className="sticky top-0 z-10 flex flex-row items-start justify-between gap-2 border-b border-gray-200 bg-white px-5 pt-5 pb-2">
              <DialogTitle className="flex-1">
                {data ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span>
                      {data.productSku ?? data.productName}
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        → {data.destinationWarehouseName}
                      </span>
                    </span>
                    {/* The inventory detail route is now keyed on the ProductV3
                        key, which every suggestion carries, so this link works
                        for v3-only suggestions too. It was previously gated on
                        the legacy v2 id and therefore dead post-cutover. */}
                    {data.productV3Key ? (
                      <a
                        href={`/inventory-management/inventory/${PRODUCT_BY_KEY_SEGMENT}?productV3Key=${encodeURIComponent(data.productV3Key)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        title={gt('Open SKU detail in a new tab', {
                          context: 'open product detail in new tab tooltip',
                        })}
                        aria-label={gt('Open SKU detail in a new tab', {
                          context: 'open product detail in new tab aria',
                        })}
                      >
                        <ArrowSquareOutIcon className="h-3.5 w-3.5" />
                      </a>
                    ) : null}
                    <CopyIdButton
                      id={data.suggestionId}
                      label={gt('Copy suggestion ID', {
                        context: 'copy id button label',
                      })}
                    />
                    {data.productV3Key ? (
                      <CopyIdButton
                        id={data.productV3Key}
                        label={gt('Copy SKU', {
                          context: 'copy id button label',
                        })}
                      />
                    ) : null}
                    <CopyIdButton
                      id={data.destinationWarehouseId}
                      label={gt('Copy destination location ID', {
                        context: 'copy id button label',
                      })}
                    />
                  </div>
                ) : (
                  <T context="suggestion detail loading">Loading...</T>
                )}
              </DialogTitle>
              <div className="flex shrink-0 items-center gap-1 text-xs text-gray-500">
                {/* Sibling prev/next — only available when the modal was
                    opened from a supplier-detail context (supplierId in URL). */}
                {siblingIds.length > 1 && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 w-7 p-0"
                      aria-label={gt('Previous suggestion', {
                        context: 'modal navigation aria',
                      })}
                      disabled={prevId == null}
                      onClick={() => prevId && gotoSibling(prevId)}
                    >
                      <CaretLeftIcon className="h-4 w-4" />
                    </Button>
                    <span className="tabular-nums">
                      {currentIdx >= 0
                        ? `${currentIdx + 1} / ${siblingIds.length}`
                        : `– / ${siblingIds.length}`}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 w-7 p-0"
                      aria-label={gt('Next suggestion', {
                        context: 'modal navigation aria',
                      })}
                      disabled={nextId == null}
                      onClick={() => nextId && gotoSibling(nextId)}
                    >
                      <CaretRightIcon className="h-4 w-4" />
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  aria-label={gt('Close', { context: 'modal close aria' })}
                  onClick={handleClose}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>

            <div className="px-5 pb-6">
              {detail.isLoading && (
                <div className="flex items-center justify-center py-12">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                </div>
              )}

              {data && (
                <div className="mt-4 space-y-6">
                  {/* Context panel — tabbed when a trigger SO exists so the
                      buyer can default to the active decision frame (SO) but
                      still hop back to the at-a-glance Need Summary. Deficits
                      degrade to just the summary (no tabs). */}
                  {data.triggerSalesOrderId ? (
                    <Tabs defaultValue="sales-order" className="w-full">
                      <TabsList>
                        <TabsTrigger value="sales-order">
                          <T context="suggestion detail tab sales order">
                            Sales Order
                          </T>
                        </TabsTrigger>
                        <TabsTrigger value="need-summary">
                          <T context="suggestion detail tab need summary">
                            Need Summary
                          </T>
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="sales-order" className="mt-3">
                        <SalesOrderInline
                          salesOrderId={data.triggerSalesOrderId}
                          highlightLineItemId={data.triggerSalesOrderLineItemId}
                        />
                      </TabsContent>
                      <TabsContent value="need-summary" className="mt-3">
                        <NeedSummaryPanel data={data} gt={gt} />
                      </TabsContent>
                    </Tabs>
                  ) : (
                    <NeedSummaryPanel data={data} gt={gt} />
                  )}

                  {/* Visual separator between the context panel (SO / Need
                      Summary) and the actionable fulfillment options. */}
                  <hr className="border-t border-gray-200" />

                  <div>
                    <h3 className="mb-3 text-sm font-medium text-gray-700">
                      <T context="suggestion detail recommended action section">
                        Recommended action
                      </T>
                    </h3>
                    {(() => {
                      // One deterministic path per line now (branch → transfer
                      // from its hub, hub → buy from its vendor). Show the
                      // effective option — the buyer's chosen, else the engine's
                      // pick — as a read-only summary. "Change source / quantity"
                      // below is the rare escape hatch.
                      const effective =
                        visibleOptions.find(
                          (o) => o.optionId === data.chosenOptionId,
                        ) ??
                        visibleOptions[0] ??
                        null;
                      if (!effective) {
                        return (
                          <p className="text-sm text-gray-400">
                            <T context="no fulfillment path">
                              No fulfillment path available for this line.
                            </T>
                          </p>
                        );
                      }
                      return (
                        <div className="space-y-3">
                          <OptionCard
                            option={effective}
                            isSelected
                            isExplicitlyChosen={
                              effective.optionId === data.chosenOptionId
                            }
                            neededQuantity={data.neededQuantity}
                            onApply={() => {}}
                            isApplying={false}
                            isAnyApplying={false}
                            readOnly
                            cart={data.cart}
                            leadTime={{
                              productV3Key: data.productSku,
                              warehouseId: data.destinationWarehouseId,
                              productLabel: data.productSku ?? data.productName,
                              overrideDays: data.leadTimeOverrideDays,
                              suggestionId: data.suggestionId,
                              highlightCartKey,
                            }}
                            gt={gt}
                          />
                          {/* A released line's plan is already on an order. */}
                          {!isReleasedStatus(data.status) && (
                            <OverrideSection
                              suggestion={data}
                              side={side}
                              highlightCartKey={highlightCartKey}
                            />
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {data.reviewedByUserName && (
                    <div className="border-t border-gray-200 pt-3 text-xs text-gray-500">
                      <T context="suggestion review info">Reviewed by</T>{' '}
                      {data.reviewedByUserName}
                      {data.reviewedAt && (
                        <span>
                          {' '}
                          {gt('on', {
                            context: 'reviewed on date preposition',
                          })}{' '}
                          {new Date(data.reviewedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
